import { html, nothing } from "lit";
import { t } from "../../i18n/index.ts";
import type { AppViewState } from "../app-view-state.ts";
import { icons } from "../icons.ts";
import { normalizeBasePath } from "../navigation.ts";
import { agentLogoUrl } from "./agents-utils.ts";
import { renderConnectCommand } from "./connect-command.ts";
import { renderLanding, SHOWCASE_CARDS, APP_LOGOS, renderAppLogo } from "./landing.ts";
import type { ShowcaseCard } from "./landing.ts";
import { hasClerkSession } from "../clerk-session-check.ts";

// ---------------------------------------------------------------------------
// Clerk managed-mode auth — state machine
// ---------------------------------------------------------------------------
//
//  States:
//    "loading"         — Clerk SDK loading (background, landing page shown)
//    "sign-in"         — show Clerk sign-in widget
//    "checking-status" — signed in, checking billing + container status
//    "billing"         — no active subscription, show pricing card
//    "billing-verify"  — returned from Stripe, verifying session
//    "provisioning"    — subscription active, provisioning container
//    "connecting"      — container running, connecting to gateway
//    "gateway-error"   — gateway is unreachable
//    "api-error"       — API call failed
//
//  Flow: landing → sign-in → checking-status → billing → Stripe → billing-verify
//        → provisioning → connecting → chat
//  Returning users skip billing: sign-in → checking-status → connecting → chat
// ---------------------------------------------------------------------------

type ManagedAuthPhase =
  | "loading"
  | "sign-in"
  | "sign-up"
  | "checking-status"
  | "billing"
  | "billing-verify"
  | "provisioning"
  | "connecting"
  | "gateway-error"
  | "api-error";

/** Trigger a Lit re-render. The state object is actually a LitElement with requestUpdate(). */
function triggerUpdate(state: AppViewState) {
  const el = state as AppViewState & { requestUpdate?: () => void };
  if (typeof el.requestUpdate === "function") el.requestUpdate();
}

let phase: ManagedAuthPhase = "loading";
let errorMessage: string | null = null;
let errorRetriable = false;
let clerkInitStarted = false;
let connectAttempted = false;
let clerkSignedIn = false;
let provisionStep = 1;
let provisioningDetail: string | null = null;
let checkoutLoading = false;
let provisionPollTimer: ReturnType<typeof setInterval> | null = null;

// Pricing features shown on the billing card
const FEATURES = [
  "Personal AI chief of staff, available 24/7",
  "WhatsApp, Telegram, Discord, Email integrations",
  "Long-term memory across every conversation",
  "Daily morning brief and proactive reminders",
  "50+ built-in skills (email triage, calendar, research, drafts...)",
  "Powered by Claude Sonnet 4 via OpenRouter",
  "Cancel anytime — no contracts",
];

const PROVISION_STEPS = [
  "Creating secure container",
  "Setting up tunnel",
  "Installing 18 skills",
  "Starting gateway",
];

/** Reset state — used when signing out or retrying */
function resetManagedAuth() {
  phase = "loading";
  errorMessage = null;
  clerkInitStarted = false;
  connectAttempted = false;
  clerkSignedIn = false;
  provisionStep = 1;
  checkoutLoading = false;
  if (provisionPollTimer) {
    clearInterval(provisionPollTimer);
    provisionPollTimer = null;
  }
}

// ---------------------------------------------------------------------------
// Clerk initialization
// ---------------------------------------------------------------------------

export async function initClerkAuth(state: AppViewState) {
  if (clerkInitStarted) return;
  clerkInitStarted = true;
  phase = "loading";
  errorMessage = null;
  triggerUpdate(state);

  try {
    const { getClerk } = await import("../clerk-auth.ts");
    const clerk = await getClerk();

    // Already signed in? Always fetch fresh credentials for this user.
    // Even if state.connected is true from a previous session, we must
    // verify the current Clerk user matches the connected gateway pod.
    // Without this, User A signs out → User B signs in → User B sees
    // User A's pod because the stale WebSocket was still open.
    if (clerk.user) {
      clerkSignedIn = true;
      // Stop stale gateway client from a previous user session.
      if (state.client) {
        state.client.stop();
        state.connected = false;
      }
      await checkStatusAndRoute(state);
      return;
    }

    // Not signed in — stay on landing (phase remains "loading")
    // Clerk is ready in background, sign-in will be triggered by CTA
    triggerUpdate(state);

    // Listen for sign-in completion using addListener (official Clerk pattern).
    // This fires when user/session state changes — including after OAuth
    // redirects and on mobile where session hydration is delayed.
    clerk.addListener(async ({ user, session }) => {
      if ((user || session) && !clerkSignedIn) {
        clerkSignedIn = true;
        // Always stop + re-fetch credentials for the new user.
        if (state.client) {
          state.client.stop();
          state.connected = false;
        }
        await checkStatusAndRoute(state);
      }
    });
  } catch (err) {
    phase = "api-error";
    errorMessage = err instanceof Error ? err.message : String(err);
    console.error("[clerk] init failed:", err);
    triggerUpdate(state);
  }
}

// ---------------------------------------------------------------------------
// Status check — determines where the user is in the funnel
// ---------------------------------------------------------------------------

async function checkStatusAndRoute(state: AppViewState) {
  phase = "checking-status";
  triggerUpdate(state);

  try {
    const { apiFetchWithJWT } = await import("../clerk-auth.ts");

    // Check for Stripe redirect (?session_id=cs_xxx)
    const url = new URL(window.location.href);
    const sessionId = url.searchParams.get("session_id");
    if (sessionId) {
      await handleBillingVerify(state, sessionId);
      return;
    }

    // Check if checkout was cancelled
    if (url.searchParams.get("canceled") === "1") {
      window.history.replaceState({}, "", window.location.pathname + window.location.hash);
      // Fall through to billing check — will show billing with cancel notice
    }

    // Check billing status
    const billingRes = await apiFetchWithJWT("/api/billing/status");
    if (!billingRes.ok) throw new Error(`Billing check failed (${billingRes.status})`);
    const billing = await billingRes.json();

    if (!billing.active) {
      phase = "billing";
      triggerUpdate(state);
      return;
    }

    // Billing active — check container status
    const containerRes = await apiFetchWithJWT("/api/container/status");
    if (!containerRes.ok) {
      // Container status 404/500 likely means not provisioned yet
      await handleProvisioning(state);
      return;
    }
    const container = await containerRes.json();

    if (container.state === "running") {
      await connectToGateway(state);
    } else if (container.state === "provisioning") {
      // Already provisioning — just poll for completion
      phase = "provisioning";
      provisionStep = container.provisioningStep || 1;
      triggerUpdate(state);
      startProvisionPolling(state);
    } else {
      // inactive, error, stopped — trigger provisioning
      await handleProvisioning(state);
    }
  } catch (err) {
    phase = "api-error";
    errorMessage = err instanceof Error ? err.message : String(err);
    errorRetriable = true;
    console.error("[managed] status check failed:", err);
    triggerUpdate(state);
  }
}

// ---------------------------------------------------------------------------
// Billing verify — after Stripe redirect
// ---------------------------------------------------------------------------

async function handleBillingVerify(state: AppViewState, sessionId: string) {
  phase = "billing-verify";
  triggerUpdate(state);

  try {
    const { apiFetchWithJWT } = await import("../clerk-auth.ts");
    const res = await apiFetchWithJWT("/api/billing/verify", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    });
    const result = await res.json();

    // Strip session_id from URL so refresh doesn't re-verify
    window.history.replaceState({}, "", window.location.pathname + window.location.hash);

    if (!result.verified) {
      phase = "api-error";
      errorMessage = `Payment verification failed: ${result.reason || "unknown"}`;
      triggerUpdate(state);
      return;
    }

    // Subscription verified → proceed to provisioning
    await handleProvisioning(state);
  } catch (err) {
    phase = "api-error";
    errorMessage = err instanceof Error ? err.message : String(err);
    console.error("[managed] billing verify failed:", err);
    triggerUpdate(state);
  }
}

// ---------------------------------------------------------------------------
// Provisioning — trigger + poll
// ---------------------------------------------------------------------------

async function handleProvisioning(state: AppViewState) {
  phase = "provisioning";
  provisionStep = 1;
  triggerUpdate(state);

  try {
    const { apiFetchWithJWT } = await import("../clerk-auth.ts");

    // Trigger provision (may 409 if already provisioned — that's fine)
    const provRes = await apiFetchWithJWT("/api/provision", { method: "POST" });
    if (!provRes.ok && provRes.status !== 409) {
      const err = await provRes.json().catch(() => ({}));
      throw new Error((err as { error?: string }).error || `Provision failed (${provRes.status})`);
    }

    // Start polling container status
    startProvisionPolling(state);
  } catch (err) {
    phase = "api-error";
    errorMessage = err instanceof Error ? err.message : String(err);
    console.error("[managed] provisioning failed:", err);
    triggerUpdate(state);
  }
}

function startProvisionPolling(state: AppViewState) {
  if (provisionPollTimer) clearInterval(provisionPollTimer);

  provisionPollTimer = setInterval(async () => {
    try {
      const { apiFetchWithJWT } = await import("../clerk-auth.ts");
      const res = await apiFetchWithJWT("/api/container/status");
      if (!res.ok) return; // Keep polling on transient errors
      const container = await res.json();
      provisionStep = container.provisioningStep || provisionStep;
      // Show K8s pod diagnostic detail when available
      if (container.provisioningDetail) {
        provisioningDetail = container.provisioningDetail;
      }
      triggerUpdate(state);

      if (container.state === "running") {
        if (provisionPollTimer) clearInterval(provisionPollTimer);
        provisionPollTimer = null;
        await connectToGateway(state);
      } else if (container.state === "error") {
        if (provisionPollTimer) clearInterval(provisionPollTimer);
        provisionPollTimer = null;
        phase = "api-error";
        errorMessage = container.error || container.provisioningDetail || "Provisioning failed. Please try again.";
        errorRetriable = container.retriable === true;
        triggerUpdate(state);
      }
    } catch {
      // Keep polling on transient errors
    }
  }, 5000);
}

// ---------------------------------------------------------------------------
// Checkout — redirect to Stripe
// ---------------------------------------------------------------------------

async function handleStartCheckout(state: AppViewState) {
  checkoutLoading = true;
  triggerUpdate(state);

  try {
    const { apiFetchWithJWT } = await import("../clerk-auth.ts");
    const origin = window.location.origin;
    const res = await apiFetchWithJWT("/api/billing/checkout", {
      method: "POST",
      body: JSON.stringify({
        successUrl: `${origin}/?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}/?canceled=1`,
      }),
    });
    if (!res.ok) throw new Error(`Checkout failed (${res.status})`);
    const { url } = await res.json();
    window.location.href = url;
  } catch (err) {
    checkoutLoading = false;
    phase = "api-error";
    errorMessage = err instanceof Error ? err.message : String(err);
    console.error("[managed] checkout failed:", err);
    triggerUpdate(state);
  }
}

// ---------------------------------------------------------------------------
// Gateway connect (existing logic, preserved)
// ---------------------------------------------------------------------------

/** Wait for state.connected to become true, polling every 200ms. */
function waitForConnection(state: AppViewState, timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    if (state.connected) { resolve(true); return; }
    const start = Date.now();
    const check = () => {
      if (state.connected) { resolve(true); return; }
      if (Date.now() - start >= timeoutMs) { resolve(false); return; }
      setTimeout(check, 200);
    };
    setTimeout(check, 200);
  });
}

/** Exponential backoff with jitter: 1s → 1.5s → 2.25s → ... → 15s max */
function retryDelay(attempt: number): number {
  return Math.min(1000 * Math.pow(1.5, attempt), 15000) + Math.random() * 1000;
}

async function connectToGateway(state: AppViewState, retries = 0) {
  if (connectAttempted && retries === 0) return;
  connectAttempted = true;
  phase = "connecting";
  // Don't clear error on retries — keep showing "Connecting..." to user
  if (retries === 0) errorMessage = null;
  triggerUpdate(state);

  const MAX_RETRIES = 5;

  try {
    const { getClerk, fetchConnectInfo } = await import("../clerk-auth.ts");
    const clerk = await getClerk();
    const info = await fetchConnectInfo(clerk);

    // Apply credentials and connect
    state.applySettings({
      ...state.settings,
      gatewayUrl: info.wsUrl,
      token: info.token,
    });

    await new Promise((r) => setTimeout(r, 100));
    state.connect();

    // Poll for connection instead of flat 4s wait — resolves as soon as connected
    const connected = await waitForConnection(state, 8000);

    // If still not connected, retry with exponential backoff
    if (!connected) {
      if (retries < MAX_RETRIES) {
        console.log(`[managed] gateway not ready, retrying (${retries + 1}/${MAX_RETRIES})...`);
        await new Promise((r) => setTimeout(r, retryDelay(retries)));
        connectAttempted = false;
        return connectToGateway(state, retries + 1);
      }
      phase = "gateway-error";
      errorMessage = "Could not reach your AI assistant. It may be starting up — please try again in a moment.";
      triggerUpdate(state);
    }
  } catch (err) {
    if (retries < MAX_RETRIES) {
      console.log(`[managed] connect error, retrying (${retries + 1}/${MAX_RETRIES})...`);
      await new Promise((r) => setTimeout(r, retryDelay(retries)));
      connectAttempted = false;
      return connectToGateway(state, retries + 1);
    }
    phase = "api-error";
    errorMessage = sanitizeErrorForUser(err instanceof Error ? err.message : String(err));
    console.error("[clerk] connect failed:", err);
    triggerUpdate(state);
  }
}

/** Strip technical jargon from error messages shown to users. */
function sanitizeErrorForUser(msg: string): string {
  return msg
    .replace(/\(\d{3}\)/g, "")                    // strip HTTP status codes like (401)
    .replace(/connect-info failed.*$/i, "Unable to connect. Please try again.")
    .replace(/Clerk/gi, "sign-in service")
    .replace(/gateway/gi, "server")
    .replace(/WebSocket/gi, "connection")
    .replace(/JWT/gi, "session")
    .trim();
}

async function handleRetry(state: AppViewState) {
  connectAttempted = false;
  errorMessage = null;
  await connectToGateway(state);
}

async function handleSignOut(state: AppViewState) {
  // Stop gateway client FIRST to prevent cross-account leakage.
  // Without this, the WebSocket stays connected to User A's pod
  // and User B sees User A's data after signing in.
  if (state.client) {
    state.client.stop();
    state.client = null;
    state.connected = false;
  }
  try {
    const { getClerk, signOut, resetClerkState } = await import("../clerk-auth.ts");
    const clerk = await getClerk();
    await signOut(clerk);
    // Clear the Clerk singleton to prevent stale user data on next sign-in
    resetClerkState();
  } catch {
    // Even on error, reset Clerk state to force a clean re-init
    try {
      const { resetClerkState } = await import("../clerk-auth.ts");
      resetClerkState();
    } catch { /* ignore */ }
  }
  resetManagedAuth();
  triggerUpdate(state);
  // Re-init to show sign-in widget
  queueMicrotask(() => initClerkAuth(state));
}

// ---------------------------------------------------------------------------
// Trigger Clerk sign-in from the landing page CTA
// ---------------------------------------------------------------------------

function showClerkSignIn(state: AppViewState) {
  phase = "sign-in";
  triggerUpdate(state);
  // Mount after DOM renders — Clerk is pre-loaded via script tag so this is fast
  requestAnimationFrame(async () => {
    try {
      const { getClerk } = await import("../clerk-auth.ts");
      const clerk = await getClerk();
      const el = document.getElementById("clerk-sign-in");
      if (el && el.childElementCount === 0) {
        (clerk as any).mountSignIn(el, {
          appearance: { variables: { colorPrimary: "#c0392b" } },
          signUpUrl: "/#sign-up",
          fallbackRedirectUrl: "/#sign-in",
          afterSignInUrl: "/#sign-in",
        });
      }
    } catch (err) {
      phase = "api-error";
      errorMessage = err instanceof Error ? err.message : String(err);
      triggerUpdate(state);
    }
  });
}

function showClerkSignUp(state: AppViewState) {
  phase = "sign-up";
  triggerUpdate(state);
  requestAnimationFrame(async () => {
    try {
      const { getClerk } = await import("../clerk-auth.ts");
      const clerk = await getClerk();
      const el = document.getElementById("clerk-sign-up");
      if (el && el.childElementCount === 0) {
        (clerk as any).mountSignUp(el, {
          appearance: { variables: { colorPrimary: "#c0392b" } },
          signInUrl: "/#sign-in",
          fallbackRedirectUrl: "/#sign-in",
          afterSignUpUrl: "/#sign-in",
        });
      }
    } catch (err) {
      phase = "api-error";
      errorMessage = err instanceof Error ? err.message : String(err);
      triggerUpdate(state);
    }
  });
}

// ---------------------------------------------------------------------------
// Render — ALITA petal logo shared across managed states
// ---------------------------------------------------------------------------

function renderAlitaLogo() {
  return html`
    <img src="/alita-logo.png" alt="ALITA" width="56" height="56" style="margin-bottom: 8px; border-radius: 14px;" />
  `;
}

function renderAlitaHeader(subtitle?: string) {
  return html`
    <div style="text-align: center; margin-bottom: 24px;">
      ${renderAlitaLogo()}
      <div style="font-size: 22px; font-weight: 700; color: #1a1a1a; letter-spacing: -0.02em;">ALITA</div>
      ${subtitle ? html`<div style="font-size: 14px; color: #6b7280; margin-top: 4px;">${subtitle}</div>` : nothing}
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Shared card wrapper style
// ---------------------------------------------------------------------------

const cardStyle = `
  max-width: 480px;
  width: 100%;
  background: #ffffff;
  border: 1px solid #e5e2dc;
  border-radius: 20px;
  padding: 40px 32px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.06);
`;

const pageStyle = `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #FAF9F5;
  padding: 24px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

// ---------------------------------------------------------------------------
// Inline provisioning banner — shown in the chat area during pod setup.
// This keeps the sidebar visible and shows progress inline instead of
// a full-page takeover.
// ---------------------------------------------------------------------------

export function isProvisioning(): boolean {
  return phase === "provisioning" || phase === "connecting";
}

export function renderProvisioningBanner() {
  if (phase !== "provisioning" && phase !== "connecting") {
    return nothing;
  }

  const steps = PROVISION_STEPS;
  const currentStep = provisionStep;
  const pct = Math.min(100, Math.round((currentStep / steps.length) * 100));
  const detail = provisioningDetail;
  const isConnecting = phase === "connecting";

  return html`
    <div style="max-width: 480px; margin: 60px auto; padding: 32px; text-align: center;">
      <div style="margin-bottom: 24px;">
        <div class="spinner" style="width: 36px; height: 36px; margin: 0 auto 16px; border-width: 3px; border-color: var(--accent, #D97757) transparent transparent transparent;"></div>
        <div style="font-size: 20px; font-weight: 700; color: var(--text, #1a1a1a);">
          ${isConnecting ? "Connecting to your assistant..." : "Setting up your assistant..."}
        </div>
        <div style="font-size: 14px; color: var(--text-muted, #9ca3af); margin-top: 4px;">
          ${isConnecting ? "Almost there!" : "This usually takes 2–3 minutes."}
        </div>
      </div>

      ${isConnecting
        ? nothing
        : html`
          <!-- Progress bar -->
          <div style="margin-bottom: 24px;">
            <div style="height: 4px; background: var(--border, #f0ede8); border-radius: 4px; overflow: hidden;">
              <div style="height: 100%; width: ${pct}%; background: linear-gradient(90deg, var(--accent, #D97757), #e8956e); border-radius: 4px; transition: width 0.5s ease;"></div>
            </div>
          </div>

          <!-- Steps -->
          <div style="display: flex; flex-direction: column; gap: 12px; text-align: left; max-width: 300px; margin: 0 auto;">
            ${steps.map((step, i) => {
              const stepNum = i + 1;
              const isDone = stepNum < currentStep;
              const isActive = stepNum === currentStep;
              return html`
                <div style="display: flex; align-items: center; gap: 10px;">
                  ${isDone
                    ? html`<div style="width: 22px; height: 22px; border-radius: 50%; background: #D9F2E6; display: flex; align-items: center; justify-content: center;">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M5 12.5l5 5 9-9" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      </div>`
                    : isActive
                      ? html`<div class="spinner" style="width: 22px; height: 22px; border-width: 2px; border-color: var(--accent, #D97757) transparent transparent transparent;"></div>`
                      : html`<div style="width: 22px; height: 22px; border-radius: 50%; border: 2px solid var(--border, #e5e2dc);"></div>`}
                  <span style="font-size: 13px; ${isDone ? "color: var(--text-muted, #9ca3af);" : isActive ? "color: var(--text, #1a1a1a); font-weight: 600;" : "color: var(--text-disabled, #c4c0ba);"}">${step}</span>
                </div>
              `;
            })}
          </div>

          ${detail
            ? html`<div style="font-size: 12px; color: var(--text-muted, #9ca3af); margin-top: 16px;">${detail}</div>`
            : nothing}
        `}
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Public render entry
// ---------------------------------------------------------------------------

export function renderLoginGate(state: AppViewState) {
  if (state.managedMode) {
    return renderManagedLoginGate(state);
  }
  return renderFullLoginGate(state);
}

function renderManagedLoginGate(state: AppViewState) {
  // Kick off Clerk initialization in background (idempotent)
  queueMicrotask(() => initClerkAuth(state));

  // Check if URL hash requests sign-up (from Clerk's "Sign up" link)
  if (!clerkSignedIn && window.location.hash === "#sign-up" && phase !== "sign-up") {
    queueMicrotask(() => showClerkSignUp(state));
  }
  // Check if URL hash requests sign-in (from Clerk's "Sign in" link on sign-up page)
  if (!clerkSignedIn && window.location.hash === "#sign-in" && phase !== "sign-in") {
    queueMicrotask(() => showClerkSignIn(state));
  }

  // Landing page — user hasn't clicked "Get started" yet
  if (!clerkSignedIn && phase === "loading") {
    return renderLanding(() => showClerkSignIn(state));
  }

  // Clerk sign-in widget
  if (!clerkSignedIn && phase === "sign-in") {
    return renderClerkSignInPage(state);
  }

  // Clerk sign-up widget
  if (!clerkSignedIn && phase === "sign-up") {
    return renderClerkSignUpPage(state);
  }

  // Checking billing/container status
  if (phase === "checking-status") {
    return renderStatusCheck();
  }

  // Billing — show pricing card
  if (phase === "billing") {
    return renderBillingCard(state);
  }

  // Billing verify — spinner
  if (phase === "billing-verify") {
    return renderBillingVerify();
  }

  // Provisioning — progress steps
  if (phase === "provisioning") {
    return renderProvisioningProgress(state);
  }

  // Connecting, errors — existing UI in a card
  return renderPostAuthCard(state);
}

// ---------------------------------------------------------------------------
// Clerk sign-in page
// ---------------------------------------------------------------------------

/**
 * Curated showcase cards for the sign-in page — one per app for variety.
 * Pulled from the full SHOWCASE_CARDS used on the landing marquee.
 */
const SIGNIN_CARDS: ShowcaseCard[] = (() => {
  // Pick one card per unique app, max 8, prioritizing the most recognizable apps
  const priority = ["gmail", "slack", "github", "notion", "googlecalendar", "linear", "stripe", "figma", "hubspot", "discord"];
  const picked: ShowcaseCard[] = [];
  const seen = new Set<string>();
  for (const app of priority) {
    if (picked.length >= 8) break;
    const card = SHOWCASE_CARDS.find((c) => c.app === app && !seen.has(c.app));
    if (card) {
      picked.push(card);
      seen.add(card.app);
    }
  }
  return picked;
})();

function renderSignInShowcaseCard(card: ShowcaseCard) {
  return html`
    <div class="signin-card">
      <div style="display:flex; align-items:center; gap:12px;">
        ${renderAppLogo(card.app, 24)}
        <div style="min-width:0; flex:1;">
          <div style="display:flex; align-items:center; gap:8px;">
            <span style="font-size:13px; font-weight:600; color:rgba(255,255,255,0.92);">${card.actionType}</span>
            <span style="
              font-size:10px; padding:2px 7px; border-radius:5px;
              background:rgba(74,222,128,0.12); color:#4ade80;
              font-weight:500; letter-spacing:0.02em;
              display:inline-flex; align-items:center; gap:3px;
            ">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6L9 17l-5-5"/></svg>
              Done
            </span>
          </div>
          <div style="font-size:11px; color:rgba(255,255,255,0.35); margin-top:1px;">${card.appLabel} · ${card.time}</div>
        </div>
      </div>
      <div style="font-size:12px; color:rgba(255,255,255,0.5); line-height:1.45; margin-top:8px; overflow:hidden; display:-webkit-box; -webkit-line-clamp:1; -webkit-box-orient:vertical;">
        ${card.detail}
      </div>
    </div>
  `;
}

function renderClerkSignInPage(state: AppViewState) {
  return html`
    <div style="
      display: flex;
      min-height: 100vh;
      background: #FAF9F5;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    ">
      <!-- Left: Sign in — clean, minimal -->
      <div style="
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 40px 24px;
        min-width: 0;
      ">
        <div style="width: 100%; max-width: 400px;">
          <div id="clerk-sign-in"></div>
          <button
            style="
              margin-top: 20px;
              background: none;
              border: none;
              color: #9ca3af;
              font-size: 13px;
              cursor: pointer;
              padding: 0;
            "
            @click=${() => {
              window.location.hash = "";
              phase = "loading";
              triggerUpdate(state);
            }}
            @mouseenter=${(e: Event) => { (e.target as HTMLElement).style.color = "#6b7280"; }}
            @mouseleave=${(e: Event) => { (e.target as HTMLElement).style.color = "#9ca3af"; }}
          >
            &larr; Back
          </button>
        </div>
      </div>

      <!-- Right: Product showcase — dark panel with real app cards -->
      <div style="
        flex: 1;
        display: none;
        flex-direction: column;
        justify-content: center;
        padding: 56px 44px;
        background: linear-gradient(145deg, #0f0f1a 0%, #141428 40%, #0d1b2a 100%);
        border-radius: 20px 0 0 20px;
        margin: 12px 0 12px 0;
        overflow: hidden;
        position: relative;
      " class="signin-showcase">
        <!-- Subtle decorative glows -->
        <div style="position:absolute; top:-120px; right:-60px; width:320px; height:320px; background:radial-gradient(circle, rgba(99,91,255,0.08) 0%, transparent 70%); pointer-events:none;"></div>
        <div style="position:absolute; bottom:-100px; left:-40px; width:280px; height:280px; background:radial-gradient(circle, rgba(217,119,87,0.06) 0%, transparent 70%); pointer-events:none;"></div>

        <div style="position:relative; z-index:1; max-width:480px;">
          <!-- Headline -->
          <div style="margin-bottom:36px;">
            <h2 style="margin:0; font-size:28px; font-weight:700; color:#ffffff; line-height:1.25; letter-spacing:-0.025em;">
              Your AI does the work.<br/>You make the decisions.
            </h2>
            <p style="margin:14px 0 0; font-size:15px; color:rgba(255,255,255,0.45); line-height:1.6;">
              ALITA connects to 30+ apps and handles tasks on autopilot — emails, meetings, code, docs, and more.
            </p>
          </div>

          <!-- Real activity cards with app logos -->
          <div style="display:flex; flex-direction:column; gap:8px;">
            ${SIGNIN_CARDS.map((c) => renderSignInShowcaseCard(c))}
          </div>

          <!-- App logo strip -->
          <div style="margin-top:28px; padding-top:20px; border-top:1px solid rgba(255,255,255,0.06);">
            <div style="display:flex; align-items:center; gap:12px; flex-wrap:wrap;">
              ${["gmail", "slack", "github", "notion", "googlecalendar", "linear", "hubspot", "figma", "stripe", "discord", "jira", "trello"].map(
                (app) => html`
                  <img
                    src=${APP_LOGOS[app] || ""}
                    alt=${app}
                    width="18"
                    height="18"
                    style="opacity:0.35; filter:brightness(1.8); border-radius:4px;"
                    loading="lazy"
                  />
                `
              )}
              <span style="font-size:12px; color:rgba(255,255,255,0.25); margin-left:2px;">+20 more</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <style>
      @media (min-width: 900px) {
        .signin-showcase { display: flex !important; }
      }
      .signin-card {
        background: rgba(255,255,255,0.04);
        border: 1px solid rgba(255,255,255,0.06);
        border-radius: 12px;
        padding: 12px 16px;
        transition: background 0.2s, border-color 0.2s;
      }
      .signin-card:hover {
        background: rgba(255,255,255,0.07);
        border-color: rgba(255,255,255,0.1);
      }
    </style>
  `;
}

// ---------------------------------------------------------------------------
// Clerk sign-up page (same layout as sign-in, keeps user in-page)
// ---------------------------------------------------------------------------

function renderClerkSignUpPage(state: AppViewState) {
  return html`
    <div style="
      display: flex;
      min-height: 100vh;
      background: #FAF9F5;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    ">
      <!-- Left: Sign up -->
      <div style="
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 40px 24px;
        min-width: 0;
      ">
        <div style="width: 100%; max-width: 400px;">
          <div id="clerk-sign-up"></div>
          <button
            style="
              margin-top: 20px;
              background: none;
              border: none;
              color: #9ca3af;
              font-size: 13px;
              cursor: pointer;
              padding: 0;
            "
            @click=${() => {
              window.location.hash = "";
              phase = "loading";
              triggerUpdate(state);
            }}
            @mouseenter=${(e: Event) => { (e.target as HTMLElement).style.color = "#6b7280"; }}
            @mouseleave=${(e: Event) => { (e.target as HTMLElement).style.color = "#9ca3af"; }}
          >
            &larr; Back
          </button>
        </div>
      </div>

      <!-- Right: Same showcase panel -->
      <div style="
        flex: 1;
        display: none;
        flex-direction: column;
        justify-content: center;
        padding: 56px 44px;
        background: linear-gradient(145deg, #0f0f1a 0%, #141428 40%, #0d1b2a 100%);
        border-radius: 20px 0 0 20px;
        margin: 12px 0 12px 0;
        overflow: hidden;
        position: relative;
      " class="signin-showcase">
        <div style="position:absolute; top:-120px; right:-60px; width:320px; height:320px; background:radial-gradient(circle, rgba(99,91,255,0.08) 0%, transparent 70%); pointer-events:none;"></div>
        <div style="position:absolute; bottom:-100px; left:-40px; width:280px; height:280px; background:radial-gradient(circle, rgba(217,119,87,0.06) 0%, transparent 70%); pointer-events:none;"></div>

        <div style="position:relative; z-index:1; max-width:480px;">
          <div style="margin-bottom:36px;">
            <h2 style="margin:0; font-size:28px; font-weight:700; color:#ffffff; line-height:1.25; letter-spacing:-0.025em;">
              Your AI does the work.<br/>You make the decisions.
            </h2>
            <p style="margin:14px 0 0; font-size:15px; color:rgba(255,255,255,0.45); line-height:1.6;">
              ALITA connects to 30+ apps and handles tasks on autopilot — emails, meetings, code, docs, and more.
            </p>
          </div>
          <div style="display:flex; flex-direction:column; gap:8px;">
            ${SIGNIN_CARDS.map((c) => renderSignInShowcaseCard(c))}
          </div>
          <div style="margin-top:28px; padding-top:20px; border-top:1px solid rgba(255,255,255,0.06);">
            <div style="display:flex; align-items:center; gap:12px; flex-wrap:wrap;">
              ${["gmail", "slack", "github", "notion", "googlecalendar", "linear", "hubspot", "figma", "stripe", "discord", "jira", "trello"].map(
                (app) => html`
                  <img
                    src=${APP_LOGOS[app] || ""}
                    alt=${app}
                    width="18"
                    height="18"
                    style="opacity:0.35; filter:brightness(1.8); border-radius:4px;"
                    loading="lazy"
                  />
                `
              )}
              <span style="font-size:12px; color:rgba(255,255,255,0.25); margin-left:2px;">+20 more</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <style>
      @media (min-width: 900px) {
        .signin-showcase { display: flex !important; }
      }
      .signin-card {
        background: rgba(255,255,255,0.04);
        border: 1px solid rgba(255,255,255,0.06);
        border-radius: 12px;
        padding: 12px 16px;
        transition: background 0.2s, border-color 0.2s;
      }
      .signin-card:hover {
        background: rgba(255,255,255,0.07);
        border-color: rgba(255,255,255,0.1);
      }
    </style>
  `;
}

// ---------------------------------------------------------------------------
// Status check spinner
// ---------------------------------------------------------------------------

function renderStatusCheck() {
  return html`
    <div style="${pageStyle}">
      <div style="${cardStyle} text-align: center;">
        ${renderAlitaHeader()}
        <div class="spinner" style="margin: 0 auto 12px; width: 32px; height: 32px;"></div>
        <div style="font-size: 15px; font-weight: 500; color: #1a1a1a;">Checking your account...</div>
        <div style="font-size: 13px; color: #6b7280; margin-top: 4px;">This only takes a moment</div>
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Billing card — pricing + "Start free trial"
// ---------------------------------------------------------------------------

function renderBillingCard(state: AppViewState) {
  return html`
    <div style="${pageStyle}">
      <div style="${cardStyle}">
        ${renderAlitaHeader("Activate your AI assistant")}

        <!-- Price -->
        <div style="text-align: center; margin-bottom: 24px;">
          <div style="display: flex; align-items: baseline; justify-content: center; gap: 6px;">
            <span style="font-size: 48px; font-weight: 700; color: #1a1a1a; letter-spacing: -0.03em;">$99</span>
            <span style="font-size: 16px; color: #6b7280;">/ month</span>
          </div>
          <div style="font-size: 14px; color: #D97757; font-weight: 600; margin-top: 4px;">
            Start with a 7-day free trial
          </div>
        </div>

        <!-- Features -->
        <ul style="list-style: none; padding: 0; margin: 0 0 28px 0;">
          ${FEATURES.map(
            (f) => html`
              <li style="display: flex; align-items: flex-start; gap: 10px; padding: 6px 0; font-size: 14px; color: #374151;">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style="flex-shrink: 0; margin-top: 1px;">
                  <circle cx="12" cy="12" r="12" fill="#D9F2E6"/>
                  <path d="M7 12.5l3 3 7-7" stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                ${f}
              </li>
            `,
          )}
        </ul>

        <!-- CTA button -->
        <button
          style="
            width: 100%;
            padding: 14px 24px;
            background: #D97757;
            color: white;
            border: none;
            border-radius: 12px;
            font-size: 16px;
            font-weight: 600;
            cursor: ${checkoutLoading ? "not-allowed" : "pointer"};
            opacity: ${checkoutLoading ? "0.7" : "1"};
            transition: background 0.15s;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
          "
          ?disabled=${checkoutLoading}
          @click=${() => handleStartCheckout(state)}
          @mouseenter=${(e: Event) => {
            if (!checkoutLoading) (e.target as HTMLElement).style.background = "#c4654a";
          }}
          @mouseleave=${(e: Event) => {
            (e.target as HTMLElement).style.background = "#D97757";
          }}
        >
          ${checkoutLoading
            ? html`<div class="spinner" style="width: 18px; height: 18px; border-width: 2px;"></div> Redirecting to checkout...`
            : "Start free trial"}
        </button>

        <p style="text-align: center; font-size: 12px; color: #9ca3af; margin-top: 12px;">
          Secure checkout by Stripe. Cancel anytime.
        </p>

        <!-- Sign out link -->
        <div style="text-align: center; margin-top: 20px; border-top: 1px solid #f0ede8; padding-top: 16px;">
          <button
            style="background: none; border: none; color: #6b7280; font-size: 13px; cursor: pointer; text-decoration: underline; text-underline-offset: 2px;"
            @click=${() => handleSignOut(state)}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Billing verify spinner
// ---------------------------------------------------------------------------

function renderBillingVerify() {
  return html`
    <div style="${pageStyle}">
      <div style="${cardStyle} text-align: center;">
        ${renderAlitaHeader()}
        <div class="spinner" style="margin: 0 auto 12px; width: 32px; height: 32px;"></div>
        <div style="font-size: 16px; font-weight: 600; color: #1a1a1a;">Verifying your payment...</div>
        <div style="font-size: 13px; color: #6b7280; margin-top: 4px;">Just a moment</div>
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Provisioning progress
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Onboarding wizard — shown during provisioning to keep users engaged
// ---------------------------------------------------------------------------

const ONBOARDING_SLIDES = [
  {
    id: "role",
    emoji: "👋",
    title: "Welcome! What's your role?",
    subtitle: "This helps ALITA personalize your experience",
    options: [
      { label: "Founder / CEO", value: "founder" },
      { label: "Executive / VP", value: "executive" },
      { label: "Manager / Team Lead", value: "manager" },
      { label: "Freelancer / Solo", value: "freelancer" },
      { label: "Other", value: "other" },
    ],
  },
  {
    id: "use_case",
    emoji: "🎯",
    title: "What will you use ALITA for?",
    subtitle: "Select your top priority — you can always change later",
    options: [
      { label: "Email & inbox management", value: "email" },
      { label: "Scheduling & calendar", value: "calendar" },
      { label: "Research & summaries", value: "research" },
      { label: "Content & social media", value: "content" },
      { label: "CRM & sales follow-ups", value: "crm" },
      { label: "General productivity", value: "general" },
    ],
  },
  {
    id: "channel",
    emoji: "💬",
    title: "How do you want to chat with ALITA?",
    subtitle: "You can connect more channels later from Settings",
    options: [
      { label: "WhatsApp", value: "whatsapp" },
      { label: "Telegram", value: "telegram" },
      { label: "Slack", value: "slack" },
      { label: "Web only (for now)", value: "web" },
    ],
  },
];

let onboardingSlide = 0;
const onboardingAnswers: Record<string, string> = {};

function renderProvisioningProgress(state: AppViewState) {
  const slide = ONBOARDING_SLIDES[onboardingSlide];
  const isLastSlide = onboardingSlide >= ONBOARDING_SLIDES.length - 1;
  const wizardDone = onboardingSlide >= ONBOARDING_SLIDES.length;

  // Progress bar for provisioning (thin bar at top)
  const provisionPct = Math.min(100, Math.round((provisionStep / PROVISION_STEPS.length) * 100));

  return html`
    <div style="${pageStyle}">
      <div style="${cardStyle}; max-width: 480px;">
        ${renderAlitaHeader()}

        <!-- Provisioning progress bar (thin, always visible) -->
        <div style="margin-bottom: 20px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
            <span style="font-size: 12px; color: #9ca3af;">Setting up your assistant...</span>
            <span style="font-size: 12px; color: #9ca3af;">${provisionPct}%</span>
          </div>
          <div style="height: 4px; background: #f0ede8; border-radius: 4px; overflow: hidden;">
            <div style="height: 100%; width: ${provisionPct}%; background: linear-gradient(90deg, #D97757, #e8956e); border-radius: 4px; transition: width 0.5s ease;"></div>
          </div>
        </div>

        ${wizardDone
          ? html`
              <!-- All slides answered — show provision steps -->
              <div style="text-align: center; margin-bottom: 20px;">
                <div style="font-size: 18px; font-weight: 700; color: #1a1a1a;">Almost ready!</div>
                <div style="font-size: 14px; color: #6b7280; margin-top: 4px;">Finishing setup...</div>
              </div>
              <div style="display: flex; flex-direction: column; gap: 12px;">
                ${PROVISION_STEPS.map((step, i) => {
                  const stepNum = i + 1;
                  const isDone = stepNum < provisionStep;
                  const isActive = stepNum === provisionStep;
                  return html`
                    <div style="display: flex; align-items: center; gap: 10px;">
                      ${isDone
                        ? html`<div style="width: 24px; height: 24px; border-radius: 50%; background: #D9F2E6; display: flex; align-items: center; justify-content: center;">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M5 12.5l5 5 9-9" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                          </div>`
                        : isActive
                          ? html`<div class="spinner" style="width: 24px; height: 24px; border-width: 2.5px; border-color: #D97757 transparent transparent transparent;"></div>`
                          : html`<div style="width: 24px; height: 24px; border-radius: 50%; border: 2px solid #e5e2dc;"></div>`}
                      <span style="font-size: 13px; ${isDone ? "color: #9ca3af;" : isActive ? "color: #1a1a1a; font-weight: 600;" : "color: #c4c0ba;"}">${step}</span>
                    </div>
                  `;
                })}
              </div>
            `
          : html`
              <!-- Onboarding slide -->
              <div style="text-align: center; margin-bottom: 20px;">
                <div style="font-size: 32px; margin-bottom: 8px;">${slide!.emoji}</div>
                <div style="font-size: 18px; font-weight: 700; color: #1a1a1a;">${slide!.title}</div>
                <div style="font-size: 13px; color: #9ca3af; margin-top: 4px;">${slide!.subtitle}</div>
              </div>

              <!-- Slide dots -->
              <div style="display: flex; justify-content: center; gap: 6px; margin-bottom: 16px;">
                ${ONBOARDING_SLIDES.map(
                  (_, i) => html`
                    <div
                      style="width: ${i === onboardingSlide ? "20px" : "6px"}; height: 6px; border-radius: 3px;
                        background: ${i === onboardingSlide ? "#D97757" : i < onboardingSlide ? "#D9F2E6" : "#e5e2dc"};
                        transition: all 0.3s ease;"
                    ></div>
                  `
                )}
              </div>

              <!-- Options -->
              <div style="display: flex; flex-direction: column; gap: 8px;">
                ${slide!.options.map(
                  (opt) => {
                    const selected = onboardingAnswers[slide!.id] === opt.value;
                    return html`
                      <button
                        style="
                          display: flex; align-items: center; padding: 12px 16px; border-radius: 10px;
                          border: 2px solid ${selected ? "#D97757" : "#f0ede8"};
                          background: ${selected ? "#FFF8F5" : "#fff"};
                          cursor: pointer; text-align: left; font-size: 14px; color: #1a1a1a;
                          transition: all 0.15s ease; font-family: inherit;
                        "
                        @click=${() => {
                          onboardingAnswers[slide!.id] = opt.value;
                          triggerUpdate(state);
                        }}
                      >
                        <span style="flex: 1; font-weight: ${selected ? "600" : "400"};">${opt.label}</span>
                        ${selected
                          ? html`<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" fill="#D97757"/>
                              <path d="M8 12.5l2.5 2.5 5-5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>`
                          : nothing}
                      </button>
                    `;
                  }
                )}
              </div>

              <!-- Next button -->
              <div style="margin-top: 16px;">
                <button
                  style="
                    width: 100%; padding: 12px; border-radius: 10px; border: none;
                    background: ${onboardingAnswers[slide!.id] ? "#D97757" : "#e5e2dc"};
                    color: ${onboardingAnswers[slide!.id] ? "#fff" : "#9ca3af"};
                    font-size: 14px; font-weight: 600; cursor: ${onboardingAnswers[slide!.id] ? "pointer" : "default"};
                    transition: all 0.15s ease; font-family: inherit;
                  "
                  ?disabled=${!onboardingAnswers[slide!.id]}
                  @click=${async () => {
                    if (!onboardingAnswers[slide!.id]) return;
                    if (isLastSlide) {
                      onboardingSlide = ONBOARDING_SLIDES.length; // wizard done
                      triggerUpdate(state);
                      // Save answers to API (fire-and-forget)
                      try {
                        const { apiFetchWithJWT } = await import("../clerk-auth.ts");
                        apiFetchWithJWT("/api/onboarding", {
                          method: "POST",
                          body: JSON.stringify({ answers: onboardingAnswers }),
                        });
                      } catch { /* best effort */ }
                    } else {
                      onboardingSlide++;
                      triggerUpdate(state);
                    }
                  }}
                >
                  ${isLastSlide ? "Finish" : "Continue"}
                </button>
              </div>

              <!-- Skip link -->
              <div style="text-align: center; margin-top: 10px;">
                <button
                  style="background: none; border: none; color: #9ca3af; font-size: 12px; cursor: pointer; font-family: inherit;"
                  @click=${() => {
                    onboardingSlide = ONBOARDING_SLIDES.length;
                    triggerUpdate(state);
                  }}
                >
                  Skip for now
                </button>
              </div>
            `}

        <!-- Sign out link -->
        <div style="text-align: center; margin-top: 20px; border-top: 1px solid #f0ede8; padding-top: 12px;">
          <button
            style="background: none; border: none; color: #6b7280; font-size: 13px; cursor: pointer; text-decoration: underline; text-underline-offset: 2px; font-family: inherit;"
            @click=${() => handleSignOut(state)}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Post-auth card — connecting / gateway-error / api-error
// ---------------------------------------------------------------------------

function renderPostAuthCard(state: AppViewState) {
  return html`
    <div style="${pageStyle}">
      <div style="${cardStyle}">
        ${renderAlitaHeader()}

        ${phase === "loading" || phase === "connecting"
          ? html`
              <div style="text-align: center; padding: 24px 0;">
                <div class="spinner" style="margin: 0 auto 12px; width: 32px; height: 32px;"></div>
                <div style="font-size: 15px; font-weight: 500; color: #1a1a1a; margin-bottom: 4px;">
                  Connecting to your assistant...
                </div>
                <div style="font-size: 13px; color: #6b7280;">
                  This usually takes a few seconds
                </div>
              </div>
            `
          : nothing}

        ${phase === "gateway-error"
          ? html`
              <div style="text-align: center; padding: 24px 0;">
                <div style="font-size: 40px; margin-bottom: 12px;">😴</div>
                <div style="font-size: 16px; font-weight: 600; color: #1a1a1a; margin-bottom: 8px;">
                  Your assistant is waking up
                </div>
                <div style="font-size: 14px; color: #6b7280; margin-bottom: 20px; line-height: 1.5;">
                  It may take a moment to start. If this persists, your assistant might be offline.
                </div>
                <button
                  style="padding: 10px 24px; background: #D97757; color: white; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; min-width: 160px;"
                  @click=${() => handleRetry(state)}
                >
                  Try again
                </button>
                <div style="margin-top: 16px;">
                  <button
                    style="background: none; border: none; color: #6b7280; font-size: 13px; cursor: pointer; text-decoration: underline; text-underline-offset: 2px;"
                    @click=${() => handleSignOut(state)}
                  >
                    Sign out
                  </button>
                </div>
              </div>
            `
          : nothing}

        ${phase === "api-error"
          ? html`
              <div style="text-align: center; padding: 24px 0;">
                <div style="font-size: 40px; margin-bottom: 12px;">${errorRetriable ? "🔄" : "⚠️"}</div>
                <div style="font-size: 16px; font-weight: 600; color: #1a1a1a; margin-bottom: 8px;">
                  ${errorRetriable ? "Setup didn't complete" : "Something went wrong"}
                </div>
                <div style="font-size: 14px; color: #6b7280; margin-bottom: 16px; line-height: 1.5;">
                  ${errorRetriable
                    ? "Your assistant ran into an issue during setup. Click below to try again — this usually fixes it."
                    : "We couldn't connect to the service. Please try again."}
                </div>
                ${errorMessage
                  ? html`<div
                      style="background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px; padding: 12px 16px; margin-bottom: 16px; text-align: left; font-size: 13px; color: #991b1b;"
                    >
                      ${errorMessage}
                    </div>`
                  : nothing}
                <button
                  style="padding: 10px 24px; background: #D97757; color: white; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; min-width: 160px;"
                  @click=${() => {
                    if (errorRetriable) {
                      // Retry provisioning directly — no need to re-authenticate
                      errorMessage = null;
                      errorRetriable = false;
                      provisioningDetail = null;
                      handleProvisioning(state);
                    } else {
                      resetManagedAuth();
                      triggerUpdate(state);
                      queueMicrotask(() => initClerkAuth(state));
                    }
                  }}
                >
                  ${errorRetriable ? "Retry setup" : "Try again"}
                </button>
                <div style="margin-top: 16px;">
                  <button
                    style="background: none; border: none; color: #6b7280; font-size: 13px; cursor: pointer; text-decoration: underline; text-underline-offset: 2px;"
                    @click=${() => handleSignOut(state)}
                  >
                    Sign out
                  </button>
                </div>
              </div>
            `
          : nothing}

        <details style="margin-top: 24px; border-top: 1px solid #f0ede8; padding-top: 16px;">
          <summary
            style="cursor: pointer; font-size: 13px; color: #6b7280; font-weight: 500; user-select: none;"
          >
            Advanced — manual connection
          </summary>
          <div style="margin-top: 16px;">${renderManualConnectionForm(state)}</div>
        </details>
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Self-hosted / full login gate (unchanged)
// ---------------------------------------------------------------------------

function renderFullLoginGate(state: AppViewState) {
  const basePath = normalizeBasePath(state.basePath ?? "");
  const faviconSrc = agentLogoUrl(basePath);

  return html`
    <div class="login-gate">
      <div class="login-gate__card">
        <div class="login-gate__header">
          <img class="login-gate__logo" src=${faviconSrc} alt="OpenClaw" />
          <div class="login-gate__title">OpenClaw</div>
          <div class="login-gate__sub">${t("login.subtitle")}</div>
        </div>
        ${renderManualConnectionForm(state)}
        ${state.lastError
          ? html`<div class="callout danger" style="margin-top: 14px;">
              <div>${state.lastError}</div>
            </div>`
          : ""}
        <div class="login-gate__help">
          <div class="login-gate__help-title">${t("overview.connection.title")}</div>
          <ol class="login-gate__steps">
            <li>
              ${t("overview.connection.step1")}${renderConnectCommand("openclaw gateway run")}
            </li>
            <li>${t("overview.connection.step2")} ${renderConnectCommand("openclaw dashboard")}</li>
            <li>${t("overview.connection.step3")}</li>
          </ol>
          <div class="login-gate__docs">
            <a
              class="session-link"
              href="https://docs.openclaw.ai/web/dashboard"
              target="_blank"
              rel="noreferrer"
              >${t("overview.connection.docsLink")}</a
            >
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderManualConnectionForm(state: AppViewState) {
  return html`
    <div class="login-gate__form">
      <label class="field">
        <span>${t("overview.access.wsUrl")}</span>
        <input
          .value=${state.settings.gatewayUrl}
          @input=${(e: Event) => {
            const v = (e.target as HTMLInputElement).value;
            state.applySettings({ ...state.settings, gatewayUrl: v });
          }}
          placeholder="ws://127.0.0.1:18789"
        />
      </label>
      <label class="field">
        <span>${t("overview.access.token")}</span>
        <div class="login-gate__secret-row">
          <input
            type=${state.loginShowGatewayToken ? "text" : "password"}
            autocomplete="off"
            spellcheck="false"
            .value=${state.settings.token}
            @input=${(e: Event) => {
              const v = (e.target as HTMLInputElement).value;
              state.applySettings({ ...state.settings, token: v });
            }}
            placeholder="OPENCLAW_GATEWAY_TOKEN (${t("login.passwordPlaceholder")})"
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === "Enter") {
                state.connect();
              }
            }}
          />
          <button
            type="button"
            class="btn btn--icon ${state.loginShowGatewayToken ? "active" : ""}"
            title=${state.loginShowGatewayToken ? "Hide token" : "Show token"}
            aria-label="Toggle token visibility"
            aria-pressed=${state.loginShowGatewayToken}
            @click=${() => {
              state.loginShowGatewayToken = !state.loginShowGatewayToken;
            }}
          >
            ${state.loginShowGatewayToken ? icons.eye : icons.eyeOff}
          </button>
        </div>
      </label>
      <label class="field">
        <span>${t("overview.access.password")}</span>
        <div class="login-gate__secret-row">
          <input
            type=${state.loginShowGatewayPassword ? "text" : "password"}
            autocomplete="off"
            spellcheck="false"
            .value=${state.password}
            @input=${(e: Event) => {
              const v = (e.target as HTMLInputElement).value;
              state.password = v;
            }}
            placeholder="${t("login.passwordPlaceholder")}"
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === "Enter") {
                state.connect();
              }
            }}
          />
          <button
            type="button"
            class="btn btn--icon ${state.loginShowGatewayPassword ? "active" : ""}"
            title=${state.loginShowGatewayPassword ? "Hide password" : "Show password"}
            aria-label="Toggle password visibility"
            aria-pressed=${state.loginShowGatewayPassword}
            @click=${() => {
              state.loginShowGatewayPassword = !state.loginShowGatewayPassword;
            }}
          >
            ${state.loginShowGatewayPassword ? icons.eye : icons.eyeOff}
          </button>
        </div>
      </label>
      <button class="btn primary login-gate__connect" @click=${() => state.connect()}>
        ${t("common.connect")}
      </button>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Auth prompt overlay — shown in-app when unauthenticated user triggers
// a gated action (Send, Install, Connect). Renders a modal with Clerk sign-in.
// ---------------------------------------------------------------------------

const HOSTED_SIGN_IN_URL = "https://accounts.alitagpt.com/sign-in";

export function renderAuthPromptOverlay(state: AppViewState) {
  if (!state.showAuthPrompt) return nothing;

  // Redirect to the hosted Clerk sign-in page (cleaner, mobile-native UI)
  // instead of mounting a modal. The user returns here after sign-in.
  // We do this once per prompt activation and clear the flag so the
  // guard doesn't fire again on re-render.
  state.showAuthPrompt = false;
  state.authPromptAction = null;

  // Safety: if the user already has a Clerk session, skip the redirect.
  // Clerk would bounce them right back here → infinite loop.
  if (hasClerkSession()) return nothing;

  const returnTo = typeof window !== "undefined" ? window.location.href : "/";
  const dest = `${HOSTED_SIGN_IN_URL}?redirect_url=${encodeURIComponent(returnTo)}`;

  if (typeof window !== "undefined") {
    queueMicrotask(() => {
      window.location.href = dest;
    });
  }

  return nothing;
}
